import clsx from "clsx";
import { MouseEvent, useRef } from "react";

import { RenderWhen } from "../../../../../../components";
import { useLocalSampleSummaryParams, useReanalyze } from "../../../../hooks/";

import {
  AnalysisAdditionalInfo,
  AnalysisStatus
} from "../../../../../../lib/enums";
import { INTEGRATION_LABELS } from "../../../../../integrations/consts";
import useIntegrationConnectionStatus from "../../../../../integrations/hooks/useIntegrationConnectionStatus";
import { IntegrationType } from "../../../../@types";
import { WarningIcon } from "../../components/icons";
import styles from "../analysis-card.module.scss";

type Props = {
  integration: IntegrationType;
  status?: string;
};

const { FAILED_UPLOAD, PROCESSING_ERROR } = AnalysisStatus ?? {};
const { FILE_OVERSIZED } = AnalysisAdditionalInfo ?? {};

const STATUS_MESSAGE_MAP: Record<string, string> = {
  [FAILED_UPLOAD]: "Previous analysis failed to upload.",
  [FILE_OVERSIZED]: "File is over size limit.",
  [PROCESSING_ERROR]: "Previous analysis failed to process."
};

const AnalyzeButton = ({ integration, status }: Props) => {
  const { hash, sourceId, relationshipId } = useLocalSampleSummaryParams();
  const { mutate } = useReanalyze();
  const isIntegrationAvailable = useRef(true);
  const { checkIntegrationConnection } = useIntegrationConnectionStatus(
    isIntegrationAvailable
  );

  const message = STATUS_MESSAGE_MAP[status ?? ""] ?? {};
  const isOversized = status === FILE_OVERSIZED;

  const handleAnalyzeClick = async (event: MouseEvent) => {
    event.stopPropagation();
    try {
      await checkIntegrationConnection(integration);
    } catch {
      console.error(
        `${
          INTEGRATION_LABELS[integration as keyof typeof INTEGRATION_LABELS]
        } connectivity problems detected. Check the integration configuration for more details.`
      );
    }
    if (!hash || isOversized || !isIntegrationAvailable.current) return;
    mutate({
      integrations: [integration],
      samples: [
        {
          hash_value: hash,
          source_id: sourceId,
          relationship_id: relationshipId
        }
      ]
    });
  };

  return (
    <div className={styles.analyze_button_container}>
      <span
        data-testid="analyze-button"
        className={clsx(styles.analyze_button, isOversized && styles.disabled)}
        onClick={handleAnalyzeClick}
      >
        Analyze
      </span>
      <RenderWhen condition={(status ?? "") in STATUS_MESSAGE_MAP}>
        <WarningIcon
          data-testid="analyze-warning"
          className={styles.warning_icon}
          message={message}
        />
      </RenderWhen>
    </div>
  );
};

export default AnalyzeButton;
